import React, { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Check, Copy, X } from "lucide-react";
import clsx from "clsx";

export const Button = ({ variant = "primary", size = "md", className, children, ...p }) => (
  <button className={clsx("cx-btn", `cx-btn-${variant}`, size === "sm" && "!px-3 !py-1.5 !text-xs", className)} {...p}>{children}</button>
);

export const Card = ({ className, children, static: isStatic = true, ...p }) => (
  <div className={clsx(isStatic ? "cx-card cx-card-static" : "cx-card", "p-5", className)} {...p}>{children}</div>
);

const badgeColor = { green: "cx-badge-green", red: "cx-badge-red", yellow: "cx-badge-yellow", gray: "cx-badge-gray", blue: "cx-badge-blue", cyan: "cx-badge-cyan" };
export const Badge = ({ color = "gray", className, children, ...p }) => (
  <span className={clsx("cx-badge", badgeColor[color], className)} {...p}>{children}</span>
);

export const CanaryBadge = ({ compact = false, ...p }) => (
  <span data-testid="canary-badge" className="cx-badge cx-badge-gray !font-semibold uppercase tracking-wide" title="Canary runtime — echo server, no GPU" {...p}>
    {compact ? "CANARY" : "CANARY — echo runtime"}
  </span>
);

export const StatusDot = ({ state }) => {
  const cls = state === true || state === "Ready" ? "cx-status-dot-ready" : state === false || state === "Failed" ? "cx-status-dot-error"
    : state == null || state === "Unknown" || state === "ScaledToZero" ? "cx-status-dot-offline" : "cx-status-dot-pending";
  return <span className={clsx("cx-status-dot", cls)} />;
};

export const Input = React.forwardRef(({ className, ...p }, ref) => <input ref={ref} className={clsx("cx-input", className)} {...p} />);
export const Select = ({ className, children, ...p }) => <select className={clsx("cx-select", className)} {...p}>{children}</select>;
export const Textarea = ({ className, ...p }) => <textarea className={clsx("cx-input font-mono text-xs min-h-[160px]", className)} {...p} />;
export const Label = ({ children, className, ...p }) => <label className={clsx("cx-label", className)} {...p}>{children}</label>;
export const Mono = ({ children, className, ...rest }) => <span className={clsx("cx-mono", className)} {...rest}>{children}</span>;

export const PageHeader = ({ title, subtitle, actions, badge, help }) => (
  <div className="cx-page-header">
    <div>
      <h1 className="cx-page-title flex items-center gap-3" data-testid="page-title">{title}{badge}{help && <a href={`/docs/guides/${help}`} className="text-cx-muted hover:text-cx-emerald text-sm font-normal border border-cx-border rounded-full w-5 h-5 inline-flex items-center justify-center" title="Open the guide for this page" data-testid="page-help">?</a>}</h1>
      {subtitle && <div className="cx-page-subtitle">{subtitle}</div>}
    </div>
    {actions && <div className="cx-page-actions">{actions}</div>}
  </div>
);

export const Skeleton = ({ className, ...p }) => <div className={clsx("cx-skeleton", className)} {...p} />;

export const EmptyState = ({ icon: Icon, title, desc, action, testid }) => (
  <div className="cx-empty-state" data-testid={testid}>
    {Icon && <div className="cx-empty-state-icon"><Icon size={36} /></div>}
    <div className="cx-empty-state-title">{title}</div>
    {desc && <div className="cx-empty-state-desc">{desc}</div>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Stat = ({ label, value, hint, testid }) => (
  <div className="cx-stat" data-testid={testid}>
    <div className="cx-stat-label">{label}</div>
    <div className="cx-stat-value">{value}</div>
    {hint && <div className="text-xs text-cx-muted mt-1">{hint}</div>}
  </div>
);

export const Tabs = ({ tabs, value, onChange }) => (
  <div className="cx-tabs" role="tablist">
    {tabs.map((t) => (
      <button key={t.value} role="tab" data-testid={`tab-${t.value}`} className={clsx("cx-tab", value === t.value && "cx-tab-active")} onClick={() => onChange(t.value)}>{t.label}</button>
    ))}
  </div>
);

export function Modal({ open, onOpenChange, title, desc, children, footer, testid, wide = false }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="cx-modal-overlay fixed inset-0 z-40" />
        <DialogPrimitive.Content className={`cx-modal fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 ${wide ? "w-[min(860px,94vw)]" : "w-[min(560px,94vw)]"} max-h-[90vh] overflow-y-auto`} data-testid={testid}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogPrimitive.Title className="cx-modal-title">{title}</DialogPrimitive.Title>
              {desc && <DialogPrimitive.Description className="cx-modal-desc">{desc}</DialogPrimitive.Description>}
            </div>
            <DialogPrimitive.Close className="cx-btn cx-btn-ghost !p-1.5" aria-label="Close"><X size={16} /></DialogPrimitive.Close>
          </div>
          <div className="mt-4">{children}</div>
          {footer && <div className="cx-modal-actions">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, desc, confirmWord, onConfirm, busy, danger = true, testid = "confirm-dialog" }) {
  const [typed, setTyped] = useState("");
  useEffect(() => { if (!open) setTyped(""); }, [open]);
  const ok = !confirmWord || typed === confirmWord;
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={title} desc={desc} testid={testid}
      footer={<>
        <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="confirm-cancel">Cancel</Button>
        <Button variant={danger ? "danger" : "primary"} disabled={!ok || busy} onClick={onConfirm} data-testid="confirm-submit">{busy ? "Working…" : "Confirm"}</Button>
      </>}>
      {confirmWord && (<div>
        <Label>Type <Mono>{confirmWord}</Mono> to confirm</Label>
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="confirm-input" autoFocus />
      </div>)}
    </Modal>
  );
}

export function CopyButton({ text, label = "Copy", testid }) {
  const [done, setDone] = useState(false);
  return (
    <button className="cx-btn cx-btn-ghost !px-2 !py-1 !text-xs inline-flex items-center gap-1" data-testid={testid}
      onClick={async () => { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}>
      {done ? <Check size={12} /> : <Copy size={12} />}{done ? "Copied" : label}
    </button>
  );
}

export const Tip = ({ content, children }) => (
  <TooltipPrimitive.Provider delayDuration={200}>
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content className="cx-card cx-card-static !p-2 text-xs z-50 max-w-xs" sideOffset={6}>{content}</TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  </TooltipPrimitive.Provider>
);

export const IdChip = ({ id, testid }) => (
  <span className="inline-flex items-center gap-1 min-w-0" data-testid={testid}><span className="cx-id" title={id}>{id}</span><CopyButton text={id} label="" testid={testid ? `${testid}-copy` : undefined} /></span>
);

export const Table = ({ head, children, testid }) => (
  <div className="cx-table-wrap">
    <table className="cx-table" data-testid={testid}>
      <thead><tr>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
